// The Build arc, one source for the three surfaces that show it: the door a
// free account meets in any step (components/builder/BuilderPackDoor), the
// landing band (components/landing/PatentLabProof) and the home
// (pages/BuilderPackPage, founder pick A 2026-09-29, #2389). Founder pick A/A,
// 2026-09-26: "for free users, the builder pack should provide the details to
// show the value of this.. how it starts with a brainstorming session". The
// copy is the mock's, verbatim; a change here changes every surface, which
// is the point. `brief` is the one-line yield the home's "What each step
// makes" list shows; `why` is the step's hint (a fact about it, never its
// place in a sequence) and `time` its honest clock.
//
// Brainstorm is the one step with a number. Founder 2026-09-28 (#2396):
// "After the brainstorm there is no specific ordering... users can start
// anywhere after the brainstorming step." The three after it are peers on
// every surface: listed in the order below because a list has to have one,
// unnumbered, under ANY_ORDER_LABEL. The two facts that used to justify an
// order stay as hints inside their own cards: the receipt is what lets the
// pitch and the application say patent pending; the paper runs for months.

import { BUILD_NAMES } from '../content/buildNames';

export const BUILDER_PACK_STEPS = [
    {
        key: 'brainstorm',
        title: BUILD_NAMES.home.label,
        short: 'Seven questions, your words',
        yields: 'Seven questions on one page: the problem you noticed, who has it, what people do today, what you would build, how it works, what would go wrong, what you have tried. Astra helps you think; what is saved is what you typed.',
        record: 'A write-up where every sentence shows which answer it came from.',
        line: 'Seven questions with Astra. Your answers, saved as you typed them.',
        why: 'Everything else is built from these.',
        time: 'About ten minutes.',
    },
    {
        key: 'patent',
        title: BUILD_NAMES.steps.patent.label,
        short: 'A filed application',
        yields: 'A search of what already exists, decided row by row by you. Then a specification with figures, in the shape of a provisional application, and the walk through filing it yourselves.',
        record: 'An application number and a filing date. "Patent pending" only once the receipt exists, then on your activities list, worded as filed.',
        line: 'A prior-art search you decide, a specification with figures, the filing walk.',
        brief: 'A search of what exists, decided by you; a specification with figures; the walk through filing it yourselves.',
        why: 'Once the receipt exists, the pitch and your application can say patent pending.',
        time: 'Minutes for the station, about an hour to file.',
    },
    {
        key: 'research',
        title: BUILD_NAMES.steps.research.label,
        short: 'A paper, with its log',
        yields: 'An area from your own record and a question that is yours. A search strategy, not a bibliography. The plan before any data. A paper to the journal\'s standard, reviewed in questions, never rewritten.',
        record: 'The log of every question the mentor asked, exported as the contributions statement journals and fairs ask for.',
        line: 'A mentor that asks and never authors; a paper with the log to prove it.',
        brief: 'A question that is yours, the plan before any data, a paper reviewed in questions, never rewritten.',
        why: 'Runs for months beside the others, so it rewards an early start.',
        time: 'Months, at three hours a week or more.',
    },
    {
        key: 'pitch',
        title: BUILD_NAMES.steps.pitch.label,
        short: 'A pitch, rehearsed',
        yields: 'The same seven answers become a short pitch deck, researched, with every number showing its source. A presenter view, a recording of your voice, three fixes after every run.',
        record: 'The deck with a source on every line, and every run\'s numbers.',
        line: 'The same answers as a sourced deck; rehearsed out loud, three fixes a run.',
        brief: 'The same answers as a sourced deck; rehearsed out loud, three fixes a run.',
        why: 'The deck reuses the seven answers and, once filed, the application number.',
        time: 'About ten minutes to write, then each run.',
    },
];

/** The one numbered step, and the peers after it (#2396). */
export const isFirstStep = (s) => s.key === 'brainstorm';

// The label over the three peers on the door, the band and the home, and
// the home's line under its path heading: one place, so the three surfaces
// say the same thing about the order (none).
export const ANY_ORDER_LABEL = 'Then, in any order';
export const ANY_ORDER_TITLE = 'Brainstorm first, then any of these';
// Names only the steps that read the seven answers (#2691): Write a paper
// asks its own seven (study.py), so the line never says each one does.
export const ANY_ORDER_INTRO = `After the seven answers, start wherever you like. ${BUILD_NAMES.steps.patent.label} and ${BUILD_NAMES.steps.pitch.label} read from them.`;

/** "four" / "three", the way the copy counts them. */
export const countWord = (n) => (n === 4 ? 'four' : 'three');

// The interview spine, for the two surfaces that ask before a record exists:
// the interview card the landing band renders to a signed-out visitor and the
// pack home's first question (no invention to read the spine from yet). The
// spine itself lives in cloud_functions/profile_manager_v2/builder.py
// (BUILDER_SPINE); builderPack.test.js pins ids, labels and asks to it, so a
// reworded question fails the suite instead of drifting. Once a record
// exists the server's copy is read instead.
export const SPINE_QUESTIONS = [
    { id: 'q1', short: 'the problem you noticed', ask: 'What is a problem you have actually run into, at school or anywhere, that bothered you enough to think about it?' },
    { id: 'q2', short: 'who has it', ask: 'Who else has this problem, and how often does it come up for them?' },
    { id: 'q3', short: 'what people do about it today', ask: 'What do people do about it right now, and where does each of those fall short?' },
    { id: 'q4', short: 'what you would build', ask: 'What would you build instead? Describe it the way you would to a friend, not to a judge.' },
    { id: 'q5', short: 'how it works, step by step', ask: 'Walk me through how it works, step by step, from the moment someone uses it.' },
    { id: 'q6', short: 'what would go wrong', ask: 'What could go wrong with it, or where would it fail?' },
    { id: 'q7', short: 'what you have tried', ask: 'What have you tried so far? A sketch, a prototype, a survey, a spreadsheet, anything.' },
];
export const SPINE_SHORTS = SPINE_QUESTIONS.map((q) => q.short);

// The season's doors, from marketing/research/2026-09-23-builder-portfolio-
// research.md as read that day: no invented dates. Conrad and Diamond show
// last cycle's; the card says so. Links go to each organisation's own site.
//
// Founder 2026-09-28 (#2398): the doors sit on the page they belong to, not
// on the home. `feature` names that page (the pitch: Conrad and Diamond; the
// paper: STS and the journals; the patent: none), and `doorsFor` is the one
// selector every page reads. `roadmap` is what "Add to my roadmap" writes:
// a title saying what the entry is, and `due` only when the date is this
// cycle's and exact. A last-cycle door says so in its title and notes and
// never carries a due date: a guessed deadline on a roadmap is a lie with a
// countdown.
export const SEASON_DOORS = {
    readOn: 'Sep 23, 2026',
    season: '2026-27',
    items: [
        // 2026-09-28: https://www.conradchallenge.org/ fails TLS on their side
        // (ERR_SSL_UNRECOGNIZED_NAME_ALERT, the certificate does not match
        // the name). The foundation's own site answers 200 and links the
        // challenge.
        { key: 'conrad', feature: 'pitch', name: 'Conrad Challenge', short: 'Conrad', when: 'Oct 30', lastCycle: true, url: 'https://conradfoundation.org/',
          note: 'Teams of 2 to 5, adult coach. A pitch with a filing behind it.',
          roadmap: { title: 'Conrad Challenge: first round' } },
        { key: 'sts', feature: 'research', name: 'Regeneron STS', short: 'STS', when: 'Nov 5, 8pm ET', lastCycle: false, url: 'https://www.societyforscience.org/regeneron-sts/',
          note: 'Seniors only. The paper is the entry.',
          roadmap: { title: 'Regeneron STS: application due', due: '2026-11-05' } },
        { key: 'journals', feature: 'research', name: 'JEI / NHSJS', short: 'JEI and NHSJS', when: 'Any time', lastCycle: false, url: 'https://emerginginvestigators.org/', url2: 'https://nhsjs.com/',
          note: 'JEI $49, 7 to 8 months, adult mentor. NHSJS free, 8 to 12 weeks.',
          roadmap: { title: 'Submit to JEI or NHSJS' } },
        { key: 'diamond', feature: 'pitch', name: 'Diamond Challenge', short: 'Diamond', when: 'Mid-January', lastCycle: true, url: 'https://diamondchallenge.org/',
          note: 'Teams of 2 to 4. A recorded five-minute pitch is accepted.',
          roadmap: { title: 'Diamond Challenge: pitch due' } },
    ],
};

/** The doors one page shows: 'pitch' | 'research' | 'patent' (none) | 'brainstorm' (none). */
export const doorsFor = (feature) => SEASON_DOORS.items.filter((d) => d.feature === feature);

/**
 * The card's caveat, trimmed to the doors on the page: "Read Sep 23, 2026.
 * Conrad and Diamond show last cycle's dates; each links its rules page."
 */
export function doorsCaveat(doors) {
    const last = doors.filter((d) => d.lastCycle).map((d) => d.short);
    const rules = doors.length === 1 ? 'it links its rules page' : 'each links its rules page';
    if (!last.length) return `Read ${SEASON_DOORS.readOn}. ${rules.charAt(0).toUpperCase()}${rules.slice(1)}.`;
    const who = last.length === 1 ? `${last[0]} shows` : `${last.slice(0, -1).join(', ')} and ${last[last.length - 1]} show`;
    return `Read ${SEASON_DOORS.readOn}. ${who} last cycle's ${last.length === 1 ? 'date' : 'dates'}; ${rules}.`;
}

/** The roadmap task's identity: one per door per season, so a second press updates, never duplicates. */
export const doorTaskId = (door) => `door-${door.key}-${SEASON_DOORS.season}`;

/**
 * What "Add to my roadmap" writes for a door (createUserTask's input). A
 * due date only when the door's date is this cycle's and exact.
 */
export function doorRoadmapTask(door) {
    const links = [door.url, door.url2].filter(Boolean).join(' ');
    if (door.lastCycle) {
        return {
            taskId: doorTaskId(door),
            title: `${door.roadmap.title}, check this year's date`,
            notes: `Last cycle's date was ${door.when}; the rules page has this year's: ${door.url}`,
        };
    }
    return {
        taskId: doorTaskId(door),
        title: door.roadmap.title,
        ...(door.roadmap.due ? { dueDate: door.roadmap.due } : {}),
        notes: `${door.when}. ${door.note} ${links}`,
    };
}

// The sample student the landing band shows (the mock's Maya, class of 2027,
// a Saturday hospital volunteer): a patient-transport tag, the test invention
// already on the landing page. Sample text, marked as such on the page.
export const SAMPLE = {
    answer: 'On Saturday shifts the wheelchairs at discharge wait on whoever is nearest. Nothing says the request exists.',
    review: {
        title: 'Review, round 1',
        row: 'Rubric row: methods, number of trials',
        quote: 'We ran the timing trial three times because that seemed like enough.',
        found: 'The plan you locked names five trials per condition. The paper reports three.',
        question: 'How did you decide the number of trials to do?',
    },
    slide: {
        line: 'Wheelchair requests at discharge go to whoever is nearest.',
        from: 'From your answer to question 3, what people do today',
        script: 'Script: "Every Saturday I watch the same thing happen at the discharge desk..."',
        timer: ['slide 2 of 9', '0:41 / 1:00', 'run 3'],
    },
};
